import { api } from "@/lib/api-typed";
import { getLogSnapshot, type LogEntry } from "./log-buffer";
import type { Schemas } from "@/types/api";

/**
 * Montar e enviar um relatório de erro à equipa.
 *
 * Os limites de tamanho espelham os do backend (`/support/error-report`): um
 * relatório que o servidor recusasse por ser grande seria um erro a esconder outro.
 */

export type ErrorReportInput = {
  /** O que a pessoa viu, na frase do ecrã («Não foi possível criar o PDF»). */
  title: string;
  /** O pormenor técnico, se houver. */
  message?: string;
  /** O que a pessoa estava a fazer, nas palavras dela. */
  note?: string;
  /** Em que parte da app (ex.: «rotulos»). */
  area?: string;
  context?: Record<string, unknown>;
};

export type ErrorReportPayload = {
  title: string;
  message: string;
  user_note: string | null;
  page_url: string;
  area: string | null;
  context: Record<string, unknown>;
  log: LogEntry[];
  client: {
    user_agent: string | null;
    viewport: string | null;
    language: string | null;
    timezone: string | null;
    app_version: string | null;
  };
};

export type ErrorReportResult = Schemas["ErrorReportOut"];

const MAX_CONTEXT_BYTES = 19_000;

// Quem tem dados no ecrã regista aqui como os descrever; na hora de enviar junta-se tudo.
const contextProviders = new Map<string, () => Record<string, unknown>>();

/** Regista o que o ecrã sabe sobre o seu estado. Devolve a função que o tira. */
export const registerReportContext = (
  key: string,
  provider: () => Record<string, unknown>,
): (() => void) => {
  contextProviders.set(key, provider);
  return () => {
    contextProviders.delete(key);
  };
};

const collectReportContext = (): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [key, provider] of contextProviders) {
    try {
      out[key] = provider();
    } catch {
      out[key] = "não foi possível ler o estado";
    }
  }
  return out;
};

/** Se o contexto for grande de mais, fica só o esqueleto — o email chega na mesma. */
const fitContext = (context: Record<string, unknown>): Record<string, unknown> => {
  try {
    if (JSON.stringify(context).length <= MAX_CONTEXT_BYTES) return context;
  } catch {
    return { truncado: true };
  }
  return { truncado: true, chaves: Object.keys(context) };
};

export const buildErrorReport = (input: ErrorReportInput): ErrorReportPayload => ({
  title: input.title.slice(0, 200),
  message: (input.message ?? "").slice(0, 4000),
  user_note: input.note?.trim() ? input.note.trim().slice(0, 2000) : null,
  page_url: `${window.location.pathname}${window.location.search}`.slice(0, 500),
  area: input.area ?? null,
  context: fitContext({ ...collectReportContext(), ...(input.context ?? {}) }),
  log: getLogSnapshot(),
  client: {
    user_agent: navigator.userAgent ?? null,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language ?? null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? null,
    app_version: import.meta.env.VITE_APP_VERSION ?? null,
  },
});

/** Limites dos anexos, iguais aos do servidor: o Brevo recusa emails grandes. */
export const MAX_ATTACHMENTS = 3;
export const MAX_ATTACHMENTS_BYTES = 3 * 1024 * 1024;
export const ATTACHMENT_ACCEPT =
  ".png,.jpg,.jpeg,.gif,.pdf,.xlsx,.xls,.csv,.txt,.zip";

/** Sai um relatório com os anexos que a pessoa juntou (capturas de ecrã, o ficheiro que deu erro). */
export const sendErrorReport = (
  payload: ErrorReportPayload,
  files: File[] = [],
): Promise<ErrorReportResult> => {
  const body = new FormData();
  body.append("report", JSON.stringify(payload));
  files.forEach((file) => body.append("files", file, file.name));
  return api.upload("/support/error-report", { form: body });
};

/** O mesmo relatório em texto, para copiar quando o envio automático não saiu. */
export const formatReportText = (payload: ErrorReportPayload): string =>
  [
    `RELATÓRIO DE ERRO — ${new Date().toLocaleString("pt-PT")}`,
    `O quê: ${payload.title}`,
    payload.message && `Detalhe: ${payload.message}`,
    payload.user_note && `A pessoa estava a: ${payload.user_note}`,
    `Onde: ${payload.page_url}${payload.area ? ` (${payload.area})` : ""}`,
    `Navegador: ${payload.client.user_agent} · ${payload.client.viewport}`,
    `Estado: ${JSON.stringify(payload.context, null, 2)}`,
    "Registo:",
    ...payload.log.map((l) => `  ${l.t} [${l.level}] ${l.text}`),
  ]
    .filter(Boolean)
    .join("\n");
